import { useEffect, useState } from 'react'
import { AdditionalFormSchema } from '../types/stammdaten.type'
import { apiRequest, isUserLoggedIn } from '../helpers'
import { Container, Row } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import KLayerNavigation from '../components/KLayerNavigation'
import { t } from '../locale'

type FieldType = 'checkbox' | 'select' | 'text' | 'upload' | 'radio'
type FieldOption = { label: string }
type Field = {
  id: string
  name: string
  type: FieldType
  options: FieldOption[]
}

export default function StammdatenAdditional() {
  const [form, setForm] = useState<AdditionalFormSchema>()
  const { _id, stammdatenType } = useParams()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [include, setInclude] = useState(true)
  const [fields, setFields] = useState<Field[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>('')

  const addField = (type: FieldType = 'text') => {
    setFields(prev => [
      ...prev,
      {
        id: uuidv4(),
        name: '',
        type,
        options: type === 'text' || type === 'upload' ? [] : [{ label: '' }],
      },
    ])
  }

  useEffect(() => {
    if (_id) {
      getForm(_id)
    }
  }, [_id])

  const getForm = async (id: string) => {
    apiRequest(`${process.env.REACT_APP_API_URL}/additional/${id}`, 'GET')
      .then(r => {
        setForm(r)
      })
      .catch(e => setError(e?.message || t('failedToLoadForm')))
  }

  useEffect(() => {
    if (!form) return
    setName(form.name ?? 'add')
    setInclude(!!form.include)

    const normalizedFields: Field[] = (form.fields || []).map((fld: any) => {
      const type = (
        ['text', 'select', 'checkbox', 'upload', 'radio'].includes(fld?.type) ? fld.type : 'text'
      ) as FieldType
      const options: FieldOption[] =
        type === 'text' || type === 'upload'
          ? []
          : (Array.isArray(fld?.options) ? fld.options : []).map((o: any) => ({
              label: o?.label ?? String(o?.value ?? o ?? ''),
            }))

      return {
        id: fld._id || uuidv4(),
        name: fld?.name || fld?.label || '',
        type,
        options,
      }
    })

    setFields(normalizedFields)
  }, [form])

  const updateField = (id: string, patch: Partial<Field>) => {
    setFields(prev => prev.map(f => (f.id === id ? { ...f, ...patch } : f)))
  }

  const removeField = (id: string) => {
    setFields(prev => prev.filter(f => f.id !== id))
  }

  const addOption = (id: string) => {
    setFields(prev =>
      prev.map(f => (f.id === id ? { ...f, options: [...(f.options || []), { label: '' }] } : f))
    )
  }

  const updateOption = (fieldId: string, index: number, which: 'label', value: string) => {
    setFields(prev =>
      prev.map(f => {
        if (f.id !== fieldId) return f
        const next = [...(f.options || [])]
        next[index] = { ...next[index], [which]: value }
        return { ...f, options: next }
      })
    )
  }

  const removeOption = (fieldId: string, index: number) => {
    setFields(prev =>
      prev.map(f => {
        if (f.id !== fieldId) return f
        const next = [...(f.options || [])]
        next.splice(index, 1)
        return { ...f, options: next }
      })
    )
  }

  const validate = (): string | null => {
    if (!name.trim()) return t('formNameRequired')
    if (fields.length === 0) return t('addAtLeastOneField')
    for (const f of fields) {
      if (!f.name.trim()) return t('everyFieldMustHaveName')
      if (f.type === 'select' || f.type === 'checkbox' || f.type === 'radio') {
        if (!f.options || f.options.length === 0)
          return `${t('field')} "${f.name || t('unnamed')}" ${t('fieldNeedsOption')}`
        for (const o of f.options) {
          if (!o.label.trim()) return `${t('optionsNeedLabel')} (${f.name})`
        }
      }
    }
    return null
  }

  const save = async () => {
    const v = validate()
    if (v) {
      setError(v)
      return
    }

    const payload = {
      stammdatenType: stammdatenType,
      name: name.trim(),
      include,
      fields: fields.map(f => ({
        name: f.name.trim(),
        type: f.type,
        ...(f.type === 'text' || f.type === 'upload' ? {} : { options: f.options }),
      })),
    }

    const isUpdate = Boolean(form?._id)

    try {
      setSaving(true)
      setError('')
      const url = isUpdate
        ? `${process.env.REACT_APP_API_URL}/additional/${form!._id}`
        : `${process.env.REACT_APP_API_URL}/additional/form`

      const method = isUpdate ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `Request failed with ${res.status}`)
      }

      const data = await res.json().catch(() => ({}))
      if (isUpdate) {
        setForm((prev: any) => ({ ...(prev || {}), ...data }))
      } else if (data?._id) {
        setForm(data)
      }

      navigate('/client/stammdaten/formular/list')
    } catch (e: any) {
      setError(e?.message || (isUpdate ? t('updateFailed') : t('saveFailed')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4 mb-5'>
            <div className='card mt-3'>
              <div className='card-body'>
                <h5 className='card-title mb-3'>
                  {form?._id ? t('editAdditionalForm') : t('createAdditionalForm')}
                </h5>

                <div className='mb-3'>
                  <label className='form-label'>{t('formName')}</label>
                  <input
                    className='form-control'
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </div>

                <div className='form-check form-switch mb-3'>
                  <input
                    id='includeSwitch'
                    className='form-check-input'
                    type='checkbox'
                    checked={include}
                    onChange={e => setInclude(e.target.checked)}
                  />
                  <label className='form-check-label' htmlFor='includeSwitch'>
                    {t('include')}
                  </label>
                </div>

                <div className='d-flex gap-2 mb-3 flex-wrap'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => addField('text')}
                  >
                    + {t('textField')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => addField('select')}
                  >
                    + {t('selectField')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => addField('checkbox')}
                  >
                    + {t('checkboxField')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => addField('upload')}
                  >
                    + {t('uploadField')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => addField('radio')}
                  >
                    + {t('radioField')}
                  </button>
                </div>

                {fields.length === 0 && <p className='text-muted'>{t('noFieldsYet')}</p>}

                {fields.map((f, idx) => (
                  <div key={f.id} className='border rounded p-3 mb-3'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <strong>
                        {t('field')} #{idx + 1}
                      </strong>
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-danger'
                        onClick={() => removeField(f.id)}
                      >
                        {t('remove')}
                      </button>
                    </div>

                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <label className='form-label'>{t('fieldName')}</label>
                        <input
                          className='form-control'
                          value={f.name}
                          onChange={e => updateField(f.id, { name: e.target.value })}
                        />
                      </div>

                      <div className='col-md-6'>
                        <label className='form-label'>{t('type')}</label>
                        <select
                          className='form-select'
                          value={f.type}
                          onChange={e => {
                            const nextType = e.target.value as FieldType
                            updateField(f.id, {
                              type: nextType,
                              options:
                                nextType === 'text' || nextType === 'upload'
                                  ? []
                                  : f.options?.length
                                    ? f.options
                                    : [{ label: '' }],
                            })
                          }}
                        >
                          <option value='text'>{t('text')}</option>
                          <option value='select'>{t('select')}</option>
                          <option value='checkbox'>{t('checkbox')}</option>
                          <option value='upload'>{t('upload')}</option>
                          <option value='radio'>{t('radio')}</option>
                        </select>
                      </div>
                    </div>

                    {(f.type === 'select' || f.type === 'checkbox' || f.type === 'radio') && (
                      <div className='mt-3'>
                        <div className='d-flex justify-content-between align-items-center'>
                          <label className='form-label m-0'>
                            {f.type === 'radio' ? t('answers') : t('options')}
                          </label>
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-primary'
                            onClick={() => addOption(f.id)}
                          >
                            + {t('addOption')}
                          </button>
                        </div>

                        {(!f.options || f.options.length === 0) && (
                          <p className='text-muted mt-2'>{t('addAtLeastOneOption')}</p>
                        )}

                        {f.options?.map((o, i) => (
                          <div key={i} className='row g-2 align-items-end mt-1'>
                            <div className='col-md-5'>
                              <label className='form-label'>{t('label')}</label>
                              <input
                                className='form-control'
                                value={o.label}
                                onChange={e => updateOption(f.id, i, 'label', e.target.value)}
                              />
                            </div>
                            {/* <div className="col-md-5">
                              <label className="form-label">Value</label>
                              <input
                                className="form-control"
                                value={o.value}
                                onChange={e => updateOption(f.id, i, 'value', e.target.value)}
                                placeholder="e.g. dell" />
                            </div> */}
                            <div className='col-md-2'>
                              <button
                                type='button'
                                className='btn btn-outline-danger w-100'
                                onClick={() => removeOption(f.id, i)}
                              >
                                {t('remove')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {error && <div className='alert alert-danger mt-2'>{error}</div>}

                <div className='d-flex gap-2 mt-3'>
                  <button
                    type='button'
                    className='btn btn-primary'
                    disabled={saving}
                    onClick={save}
                  >
                    {saving
                      ? form?._id
                        ? t('updating')
                        : t('saving')
                      : form?._id
                        ? t('updateForm')
                        : t('saveForm')}
                  </button>
                </div>
              </div>
            </div>
          </main>
        </Row>
      </Container>
    </>
  )
}
